/** Strategy specs in plain words (docs/12-ux.md §4 rule 5). */
import type { StrategySpec } from "@dukun/protocol/strategy.ts";
import { describeStrategy, engineOf, parseStrategy } from "@dukun/protocol/unified.ts";
import { coin } from "./format.ts";

export function marketWord(m: string) { return m === "spot" ? "Spot" : m === "futures" ? "Futures" : "Spot + futures"; }

export function describe(s: StrategySpec): string[] {
  const out: string[] = [];
  const mom = s.signals.filter((x) => x.type === "momentum").map((x) => (x as { days: number }).days);
  const calm = s.signals.find((x) => x.type === "calm") as { weight: number } | undefined;
  const shorts = s.signals.find((x) => x.type === "shorts") as { weight: number } | undefined;
  const carry = s.signals.find((x) => x.type === "carry") as { entryApr: number; coins: string[] } | undefined;
  const u = s.universe;
  out.push(u.mode === "manual" ? `Trades ${u.symbols.length} hand-picked coins: ${u.symbols.slice(0, 6).map((x) => coin(x.split(":")[0]!)).join(", ")}${u.symbols.length > 6 ? "…" : ""}.`
    : `Each week picks the ${u.k} most traded coins from the ${u.pool} pool (includes coins that later died).`);
  if (mom.length) out.push(`Buys coins that rose most over ${mom.join(", ")} days${shorts && s.market !== "spot" ? ` and bets down on the weakest (at ${Math.round(shorts.weight * 100)}% size)` : ""}.`);
  if (calm?.weight) out.push(`Leans long when the market is calm, more careful when it is wild.`);
  if (carry) out.push(`Earns funding on ${carry.coins.map(coin).join(", ")} when the yearly rate is above ${Math.round(carry.entryApr * 100)}% (buy spot, short the same amount of futures).`);
  out.push(`Rebalances every ${s.sizing.rebalanceHours} hour${s.sizing.rebalanceHours > 1 ? "s" : ""} aiming for ${Math.round(s.sizing.riskLevel * 100)}% yearly swings${s.market !== "spot" ? `, at most ${s.sizing.maxLeverage}× leverage` : ", no borrowing"}.`);
  const stop = s.safety?.stopAt ?? 0.3;
  out.push(`Trades smaller after a 5% drop and stops everything at −${Math.round(stop * 100)}%.`);
  return out;
}

/** Any strategy (old or new format) in plain words. */
export function describeAny(s: unknown): string[] {
  const p = parseStrategy(s);
  return p.ok ? describeStrategy(p.spec) : [];
}

/** Coins a strategy trades (for charts); the pool's best known when it picks by volume. */
export function specCoins(s: unknown): string[] {
  const p = parseStrategy(s);
  if (!p.ok) return [];
  const c = p.spec.coins;
  return c.mode === "manual" ? c.symbols.map((x) => x.split(":")[0]!) : ["BTCUSDT", "ETHUSDT", "SOLUSDT", "BNBUSDT", "XRPUSDT", "DOGEUSDT", "LUNAUSDT", "FTTUSDT"];
}

/** How a strategy trades: signals, weights or grid (null if it doesn't parse). */
export function tradeStyle(s: unknown): { by: "signals" | "weights" | "grid"; timeframe: string; barBot: boolean } | null {
  const p = parseStrategy(s);
  return p.ok ? { by: p.spec.trade.by, timeframe: p.spec.timeframe, barBot: engineOf(p.spec).kind !== "portfolio" } : null;
}
